// Phase 13 — SE Field Expense claim
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle2, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { compressAndUploadPhoto, todayIsoDate, idempKey, useGps, StatusPill } from "./_helpers";

const TYPES = [
  { code: "travel", label: "Travel" }, { code: "food", label: "Food" },
  { code: "lodging", label: "Lodging" }, { code: "misc", label: "Miscellaneous" },
  { code: "other", label: "Other" },
];

export default function ExpenseNew() {
  const nav = useNavigate();
  const gps = useGps();
  const [type, setType] = useState("travel");
  const [amount, setAmount] = useState("");
  const [expDate, setExpDate] = useState(todayIsoDate());
  const [billPhoto, setBillPhoto] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  async function uploadBill(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const ref = await compressAndUploadPhoto(f);
      setBillPhoto(ref); toast.success("Bill photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  }

  async function submit() {
    if (!amount || Number(amount) <= 0) return toast.error("Amount must be > 0");
    if (!billPhoto) return toast.error("Bill photo is required");
    setBusy(true);
    try {
      const res = await offlineMutate({
        method: "POST", path: "/expenses", entity_type: "expenses",
        body: {
          expense_type: type, amount: Number(amount), expense_date: expDate,
          bill_photo_ref: billPhoto, remarks: remarks || null, gps,
          client_ts: new Date().toISOString(),
          idempotency_key: idempKey("exp"),
          offline_captured: !navigator.onLine,
        },
      });
      if (res.immediate) toast.success("Expense submitted");
      else toast.success("Queued — will sync when online");
      nav("/money/expenses");
    } catch (e) { toast.error(e?.message || "Submit failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="expense-new-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="font-semibold flex-1">Log expense</h1>
      </div>

      <div>
        <Label>Type *</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="exp-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div>
        <Label>Amount (₹) *</Label>
        <Input data-testid="exp-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>

      <div>
        <Label>Date *</Label>
        <Input data-testid="exp-date" type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} />
      </div>

      <div>
        <Label>Bill photo *</Label>
        <input type="file" accept="image/*" capture="environment" className="hidden" id="exp-photo-input" onChange={uploadBill} />
        <label htmlFor="exp-photo-input" data-testid="exp-photo"
               className={`block w-full border rounded-md p-3 text-center cursor-pointer ${billPhoto ? "bg-emerald-50 border-emerald-500" : "bg-white border-dashed border-neutral-300"}`}>
          {billPhoto ? <span className="text-emerald-700 text-xs flex justify-center items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Bill uploaded</span>
                     : <span className="text-neutral-500 text-sm flex justify-center items-center gap-1"><Camera className="w-4 h-4" /> Tap to capture bill</span>}
        </label>
      </div>

      <div>
        <Label>Remarks</Label>
        <Textarea data-testid="exp-remarks" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>

      {gps && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800 flex items-center gap-2">
          <MapPin className="w-3 h-3" /> GPS captured
        </div>
      )}

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy} data-testid="exp-submit">
        <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit expense"}
      </Button>
    </div>
  );
}


export function ExpensesList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/expenses?page_size=100").then(r => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);
  async function resubmit(x) {
    const newAmount = window.prompt(`Current amount: ₹${x.amount}\nEnter new amount (leave blank to keep):`, "");
    try {
      await api.post(`/expenses/${x.id}/resubmit`,
        newAmount ? { amount: Number(newAmount) } : {});
      toast.success("Resubmitted");
      const { data } = await api.get("/expenses?page_size=100");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="expenses-list-page">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-lg">Expenses</h1>
        <Button size="sm" data-testid="exp-new-btn" onClick={() => nav("/money/expenses/new")}
                className="bg-emerald-600 hover:bg-emerald-700">+ New expense</Button>
      </div>
      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 text-sm border border-dashed rounded-lg p-6 text-center">No expenses yet</div>
       : items.map(x => (
         <div key={x.id} className="border rounded-lg p-3 bg-white" data-testid={`expense-row-${x.id}`}>
           <div className="flex justify-between items-start">
             <div className="min-w-0">
               <div className="font-medium truncate">{x.expense_type}</div>
               <div className="text-xs text-neutral-500 truncate">{x.remarks || "—"}</div>
               <div className="text-xs text-neutral-400 mt-1">{x.expense_date} · v{x.version}</div>
             </div>
             <div className="text-right">
               <div className="font-semibold">₹ {Number(x.amount).toLocaleString("en-IN")}</div>
               <StatusPill status={x.status} />
             </div>
           </div>
           {x.reviewer_notes && (
             <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
               📝 {x.reviewer_notes}
             </div>
           )}
           {x.status === "rejected" && (
             <Button size="sm" variant="outline" className="mt-2 w-full" data-testid={`exp-resubmit-${x.id}`} onClick={() => resubmit(x)}>
               Resubmit
             </Button>
           )}
         </div>
       ))
      }
    </div>
  );
}
